import { useChatRooms } from '@commenda/api';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { Button, LoadingIndicator } from '@commenda/kakashi';
import { ChatRoomType } from '@commenda/prisma';
import { ChatRoom } from '@commenda/ui';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useConfig } from 'context/useConfig';
import { useAffiliateViewer } from 'hooks/useAffiliateViewer';

interface GeneralThreadPanelProps {
  companyId: number;
}

export const GeneralThreadPanel = ({ companyId }: GeneralThreadPanelProps) => {
  const { user } = useConfig();
  // An affiliate's client has no Commenda counterparty on this thread, so the panel opens on the
  // Team channel. Everyone else keeps the account-manager (Support) channel alone.
  const { isAffiliateMember, isFlagOn } = useAffiliateViewer();
  const isAffiliateViewer = isFlagOn && isAffiliateMember;

  const router = useRouter();

  const { data, isLoading, refetch } = useChatRooms({ companyId });

  const generalThread = useMemo(
    () =>
      (data?.chatRooms ?? []).find(
        (room) => room.type === ChatRoomType.GENERAL_THREAD
      ),
    [data?.chatRooms]
  );

  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-border-secondary bg-bg-primary shadow-xs">
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-border-secondary px-xl">
        <span className="font-mulish text-md font-bold leading-6 text-text-primary">
          New messages
        </span>
        {isFeatureLaunched(FeatureLaunch.INBOX_NAV) && (
          <Button
            variant="brand-link"
            size="sm"
            onClick={() => router.push('/inbox')}
          >
            Open inbox
          </Button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        {isLoading && (
          <div className="flex h-full items-center justify-center">
            <LoadingIndicator size={24} />
          </div>
        )}
        {!isLoading && generalThread && user && (
          <ChatRoom
            subscriberViewerRole="CUSTOMER"
            userId={user.id}
            companyId={companyId}
            afterChange={refetch}
            chatRoomInfo={{
              chatRoomType: ChatRoomType.GENERAL_THREAD,
              chatRoomId: generalThread.id,
              companyId,
            }}
            hideInternalNotes
            hideFeatures
            hideHeader
            forceShowTabs
            includeCustomerInternalNotes={isAffiliateViewer}
            initialParentTab={
              isAffiliateViewer ? 'CUSTOMER_INTERNAL' : undefined
            }
          />
        )}
      </div>
    </div>
  );
};
